import { type ZonedDateTime } from '@internationalized/date';
import { Button, Checkbox } from '@react-spectrum/s2';
import { useCallback, useMemo, useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import { useBridgewayApi } from '@/api/BridgewayApi';
import { getInstagramChannelFromCalendars } from '@/api/ExpressApi';
import { useExpressApiContext } from '@/api/ExpressApiContext';
import { useUserId } from '@/auth/UserIdContext';
import {
	CalendarGlyph,
	EmailThumbnail,
} from '@/components/artifact/ArtifactRow';
import { BlobImage } from '@/components/blob/BlobImage';
import { useUserSettings } from '@/hooks/useUserSettings';
import { logger } from '@/logger';
import { ChatPanelLayout } from '@/spaces/chat/panels/ChatPanelLayout';
import { syncScheduledExpressPost } from '@/spaces/chat/panels/expressPostSync';
import {
	ALREADY_PASSED_NOTICE,
	DateAndTimeFields,
	MonitorRow,
} from '@/spaces/chat/panels/ProcessSchedulePanel';

import type { Action as ProcessAction } from '../../../../../bridge/types/action.ts';
import type {
	Artifact,
	InstagramPost,
	InstagramPostSchedule,
} from '../../../../../bridge/types/artifact.ts';
import type { PanelProcess } from '../../../../../bridge/types/state.ts';
import { SCHEDULABLE_ARTIFACT_TYPES } from '../../../../../bridge/utils/setupCardItems.ts';

import {
	absoluteScheduleFrom,
	futureDefaultDate,
	isItemSent,
	normalizeToUtcIsoZ,
	parseStoredTriggerTime,
	postScheduleIso,
	resolveOnActivationPublishDate,
	rowThumbnailClass,
	scheduleItemTitle,
	statusToRowLabel,
} from './scheduleUtils.ts';

interface EditScheduleSubPanelProps {
	panelProcess: PanelProcess;
	onBack?: () => void;
	onClose: () => void;
	style?: React.CSSProperties;
}

type EditState = { mode: 'schedule' | 'now'; value: ZonedDateTime };

const derivePostState = (artifact: InstagramPost): EditState => {
	const iso = postScheduleIso(artifact);
	// Reads a stored time that names no zone the way the server reads it, as UTC.
	// `parseAbsoluteToLocal` rejects that shape outright, and the row then silently
	// showed "Publish now" for a post that is in fact scheduled.
	const stored = parseStoredTriggerTime(iso);
	if (stored) return { mode: 'schedule', value: stored };
	return { mode: 'now', value: futureDefaultDate() };
};

const deriveEmailState = (action: ProcessAction | null): EditState => {
	const trigger = action?.trigger ?? null;
	if (trigger?.type === 'scheduled_once') {
		const stored = parseStoredTriggerTime(trigger.timestamp);
		return { mode: 'schedule', value: stored ?? futureDefaultDate() };
	}
	return { mode: 'now', value: futureDefaultDate() };
};

const stateEquals = (a: EditState, b: EditState): boolean => {
	if (a.mode !== b.mode) return false;
	if (a.mode === 'now') return true;
	return a.value.toDate().getTime() === b.value.toDate().getTime();
};

interface PostScheduleRowProps {
	artifact: InstagramPost;
	state: EditState;
	onChange: (next: EditState) => void;
	onPendingChange: (isPending: boolean) => void;
}

const PostScheduleRow = ({
	artifact,
	state,
	onChange,
	onPendingChange,
}: PostScheduleRowProps) => {
	const title = scheduleItemTitle(artifact);
	const thumb = artifact.content.image_urls?.[0];

	const handleModeChange = (checked: boolean) => {
		if (checked) {
			onChange({ mode: 'now', value: state.value });
		} else {
			onChange({ mode: 'schedule', value: futureDefaultDate() });
		}
	};

	return (
		<div className="flex items-stretch gap-4 py-4">
			{thumb ? (
				<BlobImage
					src={thumb}
					alt=""
					className={`${rowThumbnailClass} object-cover`}
				/>
			) : (
				<div className={`${rowThumbnailClass} bg-gray-100`} />
			)}
			<div className="flex min-w-0 flex-1 flex-col gap-3">
				<p className="line-clamp-1 text-lg font-bold text-gray-900">{title}</p>
				<div className="flex min-h-12 items-center">
					{state.mode === 'schedule' ? (
						<DateAndTimeFields
							value={state.value}
							onChange={(value) => onChange({ mode: 'schedule', value })}
							onPendingChange={onPendingChange}
						/>
					) : (
						<div className="flex flex-wrap items-center gap-2">
							<CalendarGlyph muted />
							<span className="text-base text-gray-700">Publishes shortly</span>
						</div>
					)}
				</div>
				<Checkbox isSelected={state.mode === 'now'} onChange={handleModeChange}>
					Publish now
				</Checkbox>
			</div>
		</div>
	);
};

interface EmailScheduleRowProps {
	artifact: Artifact;
	state: EditState;
	onChange: (next: EditState) => void;
	onPendingChange: (isPending: boolean) => void;
}

const EmailScheduleRow = ({
	artifact,
	state,
	onChange,
	onPendingChange,
}: EmailScheduleRowProps) => {
	const title = scheduleItemTitle(artifact);

	const handleModeChange = (checked: boolean) => {
		if (checked) {
			onChange({ mode: 'now', value: state.value });
		} else {
			onChange({ mode: 'schedule', value: futureDefaultDate() });
		}
	};

	return (
		<div className="flex items-stretch gap-4 py-4">
			<EmailThumbnail artifact={artifact} />
			<div className="flex min-w-0 flex-1 flex-col gap-3">
				<p className="line-clamp-1 text-lg font-bold text-gray-900">{title}</p>
				<div className="flex min-h-12 items-center">
					{state.mode === 'schedule' ? (
						<DateAndTimeFields
							value={state.value}
							onChange={(value) => onChange({ mode: 'schedule', value })}
							minLeadMs={0}
							tooSoonNotice={ALREADY_PASSED_NOTICE}
							onPendingChange={onPendingChange}
						/>
					) : (
						<div className="flex flex-wrap items-center gap-2">
							<CalendarGlyph muted />
							<span className="text-base text-gray-700">Sends shortly</span>
						</div>
					)}
				</div>
				<Checkbox isSelected={state.mode === 'now'} onChange={handleModeChange}>
					Send now
				</Checkbox>
			</div>
		</div>
	);
};

interface SentReadonlyRowProps {
	artifact: Artifact;
	action: ProcessAction | null;
	processStatus: PanelProcess['status'];
	thumbnail: React.ReactNode;
}

const SentReadonlyRow = ({
	artifact,
	action,
	processStatus,
	thumbnail,
}: SentReadonlyRowProps) => {
	const title = scheduleItemTitle(artifact);
	const { label } = statusToRowLabel(artifact, action, processStatus);
	return (
		<div className="flex items-stretch gap-4 py-4 opacity-60">
			<div className="shrink-0">{thumbnail}</div>
			<div className="flex min-w-0 flex-1 flex-col gap-2 self-center">
				<p className="line-clamp-1 text-lg font-bold text-gray-900">{title}</p>
				<div className="flex items-center gap-2 text-base text-gray-700">
					<CalendarGlyph />
					<span>{label}</span>
				</div>
			</div>
		</div>
	);
};

export const EditScheduleSubPanel = ({
	panelProcess,
	onBack,
	onClose,
	style,
}: EditScheduleSubPanelProps) => {
	const bridgeApi = useBridgeApi();
	const bridgewayApi = useBridgewayApi();
	const userId = useUserId();
	const { expressApi } = useExpressApiContext();
	const { settings } = useUserSettings();
	const [isSaving, setIsSaving] = useState(false);
	const [saveError, setSaveError] = useState<string | null>(null);

	const children = useMemo<Artifact[]>(
		() => panelProcess.children ?? [],
		[panelProcess.children],
	);
	const actions = useMemo<ProcessAction[]>(
		() => panelProcess.actions ?? [],
		[panelProcess.actions],
	);

	const schedulableChildren = useMemo(
		() => children.filter((c) => SCHEDULABLE_ARTIFACT_TYPES.has(c.type)),
		[children],
	);

	const emailActionsByRef = useMemo(() => {
		const map = new Map<string, ProcessAction>();
		for (const action of actions) {
			if (action.type !== 'send_email_to_contacts') continue;
			const ref = (action.config as { artifact_ref?: string })?.artifact_ref;
			if (ref) map.set(ref, action);
		}
		return map;
	}, [actions]);

	const postArtifacts = useMemo(
		() =>
			schedulableChildren.filter(
				(c) => c.type === 'instagram_post',
			) as InstagramPost[],
		[schedulableChildren],
	);

	const emailArtifacts = useMemo(
		() =>
			schedulableChildren.filter(
				(c) => c.type === 'email_html' || c.type === 'email_plaintext',
			),
		[schedulableChildren],
	);

	const monitorActions = useMemo(
		() =>
			actions.filter((a) => a.type === 'monitor_instagram_comments_and_reply'),
		[actions],
	);

	const initialPostStates = useMemo(() => {
		const map: Record<string, EditState> = {};
		for (const post of postArtifacts) {
			if (isItemSent(post, null)) continue;
			map[post.id] = derivePostState(post);
		}
		return map;
	}, [postArtifacts]);

	const initialEmailStates = useMemo(() => {
		const map: Record<string, EditState> = {};
		for (const email of emailArtifacts) {
			const action = email.ref
				? (emailActionsByRef.get(email.ref) ?? null)
				: null;
			if (isItemSent(email, action)) continue;
			if (!action) continue;
			map[email.id] = deriveEmailState(action);
		}
		return map;
	}, [emailArtifacts, emailActionsByRef]);

	const [postEdits, setPostEdits] =
		useState<Record<string, EditState>>(initialPostStates);
	const [emailEdits, setEmailEdits] =
		useState<Record<string, EditState>>(initialEmailStates);

	const setPostEdit = useCallback((id: string, next: EditState) => {
		setPostEdits((prev) => ({ ...prev, [id]: next }));
	}, []);
	const setEmailEdit = useCallback((id: string, next: EditState) => {
		setEmailEdits((prev) => ({ ...prev, [id]: next }));
	}, []);

	// Rows showing a pick the control refused to store. This panel commits on
	// Save rather than on each pick, so without this Save would write the last
	// accepted time, close, and discard the time the customer is looking at.
	const [pendingRowIds, setPendingRowIds] = useState<ReadonlySet<string>>(
		new Set(),
	);
	const setRowPending = useCallback((id: string, isPending: boolean) => {
		setPendingRowIds((prev) => {
			if (prev.has(id) === isPending) return prev;
			const next = new Set(prev);
			if (isPending) next.add(id);
			else next.delete(id);
			return next;
		});
	}, []);

	const dirtyPosts = useMemo(() => {
		const out: { id: string; next: EditState }[] = [];
		for (const [id, next] of Object.entries(postEdits)) {
			const initial = initialPostStates[id];
			if (!initial || !stateEquals(initial, next)) out.push({ id, next });
		}
		return out;
	}, [postEdits, initialPostStates]);

	const dirtyEmails = useMemo(() => {
		const out: { id: string; next: EditState }[] = [];
		for (const [id, next] of Object.entries(emailEdits)) {
			const initial = initialEmailStates[id];
			if (!initial || !stateEquals(initial, next)) out.push({ id, next });
		}
		return out;
	}, [emailEdits, initialEmailStates]);

	const hasDirty = dirtyPosts.length > 0 || dirtyEmails.length > 0;
	const hasPendingPick = pendingRowIds.size > 0;

	const handleSave = useCallback(async () => {
		setSaveError(null);
		// Posts whose schedule changed and that Express already holds. Express
		// publishes from its own copy of the schedule, so a row written only to the
		// artifact leaves Express on the old date and the two then disagree.
		const toPushToExpress: InstagramPost[] = [];
		for (const { id, next } of dirtyPosts) {
			// "Publish now" stores a real instant here rather than the dateless
			// on_activation kind. This panel only ever renders for a campaign that is
			// already active, so no activation follows to resolve a dateless row, and
			// every reader of the row needs a date to show. The instant stays inside
			// SCHEDULE_THRESHOLD_MS, so the Express push below still reads the row as
			// a publish-now row and resolves it again once the slides are uploaded.
			const schedule: InstagramPostSchedule =
				next.mode === 'now'
					? absoluteScheduleFrom(new Date(resolveOnActivationPublishDate()))
					: absoluteScheduleFrom(next.value.toDate());
			bridgeApi.sendAction({
				type: 'do_edit_artifact',
				artifact_id: id,
				edits: { schedule, scheduled_at: new Date().toISOString() },
			});
			const post = postArtifacts.find((p) => p.id === id);
			if (
				post &&
				post.status === 'scheduled' &&
				post.express_metadata?.post_id
			) {
				toPushToExpress.push({ ...post, schedule });
			}
		}
		for (const { id, next } of dirtyEmails) {
			const email = emailArtifacts.find((e) => e.id === id);
			const action = email?.ref ? emailActionsByRef.get(email.ref) : null;
			if (!action) continue;
			if (next.mode === 'now') {
				bridgeApi.sendAction({
					type: 'do_edit_process_action',
					action_id: action.id,
					updates: { trigger: { type: 'on_activation' } },
				});
			} else {
				bridgeApi.sendAction({
					type: 'do_edit_process_action',
					action_id: action.id,
					updates: {
						trigger: {
							type: 'scheduled_once',
							timestamp: normalizeToUtcIsoZ(next.value.toDate().toISOString()),
						},
					},
				});
			}
		}
		if (toPushToExpress.length === 0) {
			bridgeApi.sendAction({ type: 'do_pop_chat_panel' });
			return;
		}
		setIsSaving(true);
		try {
			const channelInfo = getInstagramChannelFromCalendars(
				settings?.express_connection?.calendar,
			);
			if (!expressApi || !channelInfo || !userId) {
				throw new Error('Missing Express connection details');
			}
			for (const post of toPushToExpress) {
				await syncScheduledExpressPost({
					expressApi,
					channelInfo,
					bridgewayApi,
					userId,
					artifact: post,
				});
			}
			bridgeApi.sendAction({ type: 'do_pop_chat_panel' });
		} catch (error) {
			logger.error('Failed to update the scheduled Express post on save', {
				process_id: panelProcess.id,
				error: error instanceof Error ? error.message : String(error),
			});
			setSaveError('Saved, but updating the scheduled post failed. Try again.');
		} finally {
			setIsSaving(false);
		}
	}, [
		bridgeApi,
		bridgewayApi,
		dirtyPosts,
		dirtyEmails,
		emailArtifacts,
		emailActionsByRef,
		expressApi,
		panelProcess.id,
		postArtifacts,
		settings,
		userId,
	]);

	const actionRenderer = () => (
		<div className="ml-auto flex items-center gap-2">
			{saveError && <span className="text-sm text-red-700">{saveError}</span>}
			{!saveError && hasPendingPick && (
				<span className="text-sm text-gray-700" data-testid="pending-pick-hint">
					Fix the time above to save.
				</span>
			)}
			<Button
				type="button"
				variant="accent"
				isDisabled={!hasDirty || hasPendingPick || isSaving}
				isPending={isSaving}
				onPress={handleSave}
			>
				Save Changes
			</Button>
		</div>
	);

	return (
		<ChatPanelLayout
			title="Schedule"
			isLoading={false}
			onBack={onBack}
			onClose={onClose}
			actionRenderer={actionRenderer}
			style={style}
		>
			<div className="flex flex-col divide-y divide-gray-200 pt-2">
				{postArtifacts.map((artifact) => {
					if (isItemSent(artifact, null)) {
						const thumb = artifact.content.image_urls?.[0];
						return (
							<SentReadonlyRow
								key={artifact.id}
								artifact={artifact}
								action={null}
								processStatus={panelProcess.status}
								thumbnail={
									thumb ? (
										<BlobImage
											src={thumb}
											alt=""
											className={`${rowThumbnailClass} object-cover`}
										/>
									) : (
										<div className={`${rowThumbnailClass} bg-gray-100`} />
									)
								}
							/>
						);
					}
					const state = postEdits[artifact.id];
					if (!state) return null;
					return (
						<PostScheduleRow
							key={artifact.id}
							artifact={artifact}
							state={state}
							onChange={(next) => setPostEdit(artifact.id, next)}
							onPendingChange={(isPending) =>
								setRowPending(artifact.id, isPending)
							}
						/>
					);
				})}
				{emailArtifacts.map((artifact) => {
					const action = artifact.ref
						? (emailActionsByRef.get(artifact.ref) ?? null)
						: null;
					if (isItemSent(artifact, action)) {
						return (
							<SentReadonlyRow
								key={artifact.id}
								artifact={artifact}
								action={action}
								processStatus={panelProcess.status}
								thumbnail={<EmailThumbnail artifact={artifact} />}
							/>
						);
					}
					if (!action) return null;
					const state = emailEdits[artifact.id];
					if (!state) return null;
					return (
						<EmailScheduleRow
							key={artifact.id}
							artifact={artifact}
							state={state}
							onChange={(next) => setEmailEdit(artifact.id, next)}
							onPendingChange={(isPending) =>
								setRowPending(artifact.id, isPending)
							}
						/>
					);
				})}
				{monitorActions.map((action) => (
					<MonitorRow key={action.id} />
				))}
			</div>
		</ChatPanelLayout>
	);
};
